import type { SessionView } from '@/api/hooks/shared/useSession'
import { decodeClaims, getAccessToken, isDemoSession, type Claims } from '@/auth/oidc'
import type { Role } from '@/data/types'
import type { BookWorkspaceId, WorkspaceMode } from './domain'
import { workspaceById } from './routes'

/**
 * Who may run the book, and which pages they get.
 *
 * This console is for agent tiers. Company Master runs the house-side console
 * with its own Betfair and flag-final screens and does not sign in here;
 * Aggregate, staff Users and members have no book at all. What is left is four
 * tiers that all get the same six pages, so this is an admission check, not a
 * per-role menu matrix.
 *
 * The level names are the ones operators and support say out loud, which is
 * what makes this table checkable against the console it replaces.
 */
export const ADMITTED_ACCESS_LEVELS = [
  'TripleMaster',
  'DoubleMaster',
  'SuperMaster',
  'Master',
] as const

export type LegacyAccessLevel = (typeof ADMITTED_ACCESS_LEVELS)[number]

export interface LegacyProfile {
  accessLevel: LegacyAccessLevel
}

const ROLE_TO_LEVEL: Record<Role, LegacyAccessLevel | null> = {
  'company-master': null,
  'triple-master': 'TripleMaster',
  'double-master': 'DoubleMaster',
  'super-master': 'SuperMaster',
  master: 'Master',
  player: null,
}

/** Keycloak hierarchy depth. 1 = Company Master, 6 = member — both refused. */
const LEVEL_BY_DEPTH: Record<string, LegacyAccessLevel> = {
  '2': 'TripleMaster',
  '3': 'DoubleMaster',
  '4': 'SuperMaster',
  '5': 'Master',
}

/** Scopes that end the check immediately, whatever else the token says. */
const DENIED_SCOPES = new Set(['player', 'member'])

function isAdmittedLevel(value: unknown): value is LegacyAccessLevel {
  return (
    typeof value === 'string' && (ADMITTED_ACCESS_LEVELS as readonly string[]).includes(value)
  )
}

/** Modes this profile may see and open, in workspace order. */
export function visibleModes(
  workspace: BookWorkspaceId,
  profile: LegacyProfile | null,
): WorkspaceMode[] {
  if (!profile) return []
  return workspaceById(workspace).modes.map((mode) => mode.id)
}

export function canOpenMode(
  workspace: BookWorkspaceId,
  mode: WorkspaceMode,
  profile: LegacyProfile | null,
): boolean {
  return visibleModes(workspace, profile).includes(mode)
}

/** First mode this profile may open, so a workspace link never lands on a denial. */
export function landingMode(
  workspace: BookWorkspaceId,
  profile: LegacyProfile | null,
): WorkspaceMode | null {
  return visibleModes(workspace, profile)[0] ?? null
}

export interface LegacyProfileInput {
  demo: boolean
  claims: Claims | null
  sessionRole?: Role
  sessionScopes?: string[]
}

/**
 * Map the Strix session onto an admitted access level, or refuse it.
 *
 * Preference order: an explicit `accessLevel` claim, then hierarchy depth, then
 * the account role. A token that names a tier this console does not admit is a
 * refusal, not a fallback to a lower tier — sliding a Company Master into the
 * Master menu would be inventing an entitlement.
 */
export function resolveLegacyProfile(input: LegacyProfileInput): LegacyProfile | null {
  const scopes = [...(input.claims?.roles ?? []), ...(input.sessionScopes ?? [])].map((s) =>
    s.toLowerCase(),
  )
  if (scopes.some((scope) => DENIED_SCOPES.has(scope))) return null

  const rawLevel = input.claims?.raw?.accessLevel
  if (typeof rawLevel === 'string' && rawLevel.trim()) {
    return isAdmittedLevel(rawLevel) ? { accessLevel: rawLevel } : null
  }

  const depth = input.claims?.hierarchyLevel
  if (depth) {
    const byDepth = LEVEL_BY_DEPTH[depth]
    return byDepth ? { accessLevel: byDepth } : null
  }

  if (input.sessionRole) {
    const byRole = ROLE_TO_LEVEL[input.sessionRole]
    return byRole ? { accessLevel: byRole } : null
  }

  // Demo carries no token and no role until the fixture session loads; the
  // lowest admitted tier is the safest stand-in.
  return input.demo ? { accessLevel: 'Master' } : null
}

export function currentLegacyProfile(session?: SessionView): LegacyProfile | null {
  return resolveLegacyProfile({
    demo: isDemoSession(),
    claims: decodeClaims(getAccessToken()),
    sessionRole: session?.account.role,
    sessionScopes: session?.session.scopes,
  })
}
